import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Settings as SettingsIcon,
  Upgrade as UpgradeIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { 
  getUserSubscription, 
  createCustomerPortalSession, 
  hasActiveSubscription,
  isPremiumUser,
  getSubscriptionStatusMessage,
  subscriptionPlans 
} from '../services/stripeService';

interface SubscriptionData {
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  subscription_status?: string;
  subscription_plan?: string;
  subscription_tier?: string;
  subscription_expires_at?: string;
  cancel_at_period_end?: boolean;
}

const SubscriptionManagement: React.FC = () => {
  const { user } = useAuth();
  const { cancelAtPeriodEnd, subscriptionPeriodEndAt } = useSubscription();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadSubscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadSubscription = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const data = await getUserSubscription(user.id);
      setSubscription(data);
    } catch (err) {
      setError('Failed to load subscription information');
      console.error('Error loading subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!user) return;

    try {
      setPortalLoading(true);
      setError(null);
      await createCustomerPortalSession(user.id);
    } catch (err) {
      setError('Failed to open subscription management portal');
      console.error('Error opening portal:', err);
    } finally {
      setPortalLoading(false);
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active':
      case 'trialing':
        return 'success';
      case 'past_due':
        return 'warning';
      case 'canceled':
      case 'incomplete':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'active':
      case 'trialing':
        return <CheckCircleIcon />;
      case 'canceled':
      case 'incomplete':
        return <CancelIcon />;
      default:
        return null;
    }
  };

  const formatSubscriptionPlan = (plan?: string) => {
    switch (plan) {
      case 'monthly':
        return 'Premium Monthly';
      case 'annual':
        return 'Premium Annual';
      default:
        return 'Free Plan';
    }
  };

  const getCurrentPlanDetails = () => {
    if (!subscription?.subscription_plan) return null;
    return subscriptionPlans.find(plan => plan.id === subscription.subscription_plan);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const isActive = hasActiveSubscription(subscription?.subscription_status);
  const isPremium = isPremiumUser(subscription?.subscription_tier, subscription?.subscription_status, subscription?.subscription_expires_at);
  const currentPlan = getCurrentPlanDetails();
  const statusMessage = getSubscriptionStatusMessage(subscription?.subscription_status, subscription?.subscription_expires_at);

  return (
    <Container maxWidth="lg" sx={{ py: 4, pb: 8 }}>
      <Typography variant="h4" gutterBottom>
        Subscription Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Current Subscription Status */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>
                  Current Plan
                </Typography>
                {isPremium && <StarIcon color="primary" />}
              </Box>

              <Box mb={3}>
                <Typography variant="h3" component="div" gutterBottom>
                  {formatSubscriptionPlan(subscription?.subscription_plan)}
                </Typography>
                <Chip
                  icon={getStatusIcon(subscription?.subscription_status) || undefined}
                  label={statusMessage}
                  color={getStatusColor(subscription?.subscription_status) as any}
                  sx={{ mr: 1 }}
                />
                {subscription?.subscription_tier && (
                  <Chip
                    label={subscription.subscription_tier.toUpperCase()}
                    variant="outlined"
                    color={isPremium ? "primary" : "default"}
                    sx={{ mr: 1 }}
                  />
                )}
                {cancelAtPeriodEnd && (
                  <Chip
                    label="CANCELING AT PERIOD END"
                    variant="outlined"
                    color="warning"
                    size="small"
                  />
                )}
              </Box>

              {currentPlan && (
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom>
                    Plan Details
                  </Typography>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    ${currentPlan.price} per {currentPlan.interval}
                  </Typography>
                  {(subscription?.subscription_expires_at || subscriptionPeriodEndAt) && (
                    <Typography variant="body2" color="text.secondary">
                      {subscription?.subscription_status === 'canceled' ? 'Canceled on' : 
                       cancelAtPeriodEnd ? 'Will cancel on' :
                       isActive ? 'Renews' : 'Expires'} on{' '}
                      {cancelAtPeriodEnd && subscriptionPeriodEndAt ? 
                        new Date(subscriptionPeriodEndAt).toLocaleDateString() :
                        subscription?.subscription_expires_at ? 
                          new Date(subscription.subscription_expires_at).toLocaleDateString() : 
                          'Unknown'}
                    </Typography>
                  )}
                  
                  {cancelAtPeriodEnd && subscriptionPeriodEndAt && (
                    <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Subscription Ending:</strong> Your premium access will end on{' '}
                        <strong>{new Date(subscriptionPeriodEndAt).toLocaleDateString()}</strong> at{' '}
                        {new Date(subscriptionPeriodEndAt).toLocaleTimeString()}.
                        You can reactivate anytime before this date.
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Box display="flex" gap={2} flexWrap="wrap">
                {isActive ? (
                  <Button
                    variant="contained"
                    startIcon={<SettingsIcon />}
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                    size="large"
                  >
                    {portalLoading ? 'Loading...' : 'Manage Subscription'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<UpgradeIcon />}
                    href="/pricing"
                    size="large"
                  >
                    {subscription?.subscription_status === 'canceled' ? 'Subscribe Again' : 'Upgrade to Premium'}
                  </Button>
                )}
              </Box>

              {isActive && (
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    Use the Stripe Customer Portal to manage your subscription, update payment methods, 
                    view invoices, and cancel your subscription if needed.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Subscription Features */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {isPremium ? 'Premium Features' : 'Upgrade Benefits'}
              </Typography>
              <Box>
                {(currentPlan?.features || [
                  'Unlimited goals and tracking',
                  'Advanced talent tree system',
                  'Detailed analytics and insights',
                  'Priority support',
                  'Export your data'
                ]).map((feature, index) => (
                  <Box key={index} display="flex" alignItems="center" mb={1}>
                    <CheckCircleIcon 
                      color={isPremium ? "success" : "disabled"} 
                      sx={{ mr: 1, fontSize: 16 }} 
                    />
                    <Typography variant="body2" color={isPremium ? "text.primary" : "text.secondary"}>
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SubscriptionManagement; 